/*
 * Two colours for every agent-attention surface. Red: you're the blocker or
 * it broke (approval, question, failed, finished with problems). Green:
 * finished clean. A group is red if any member is red. Running turns are
 * grey and never a signal. The kind icon always carries the meaning too,
 * because red and green are the pair most often confused.
 */
import {
  AlertTriangle,
  Check,
  Hand,
  MessageSquare,
  type LucideIcon
} from "lucide-react";

import { markdownToPlainText } from "@/features/playground/utils/markdownPlainText";

import type { AttentionItem, SignalKind } from "./types";

export type SignalTone = "red" | "green";

/** A signal's display kind: a finished turn with problems reads as its own kind. */
export type SignalFace = SignalKind | "problems";

type Signal = Pick<AttentionItem, "kind" | "issues">;

export const signalFace = (s: Signal): SignalFace =>
  s.kind === "finished" && s.issues?.trim() ? "problems" : s.kind;

export const signalTone = (s: Signal): SignalTone =>
  signalFace(s) === "finished" ? "green" : "red";

/** Red if any member is red, green if all are clean, null when there are none. */
export const groupTone = (signals: Signal[]): SignalTone | null => {
  if (!signals.length) return null;
  return signals.some((s) => signalTone(s) === "red") ? "red" : "green";
};

export const SIGNAL_META: Record<
  SignalFace,
  { label: string; action: string; icon: LucideIcon; line: string }
> = {
  approval: {
    label: "APPROVAL NEEDED",
    action: "Review",
    icon: Hand,
    line: "Needs your approval"
  },
  question: {
    label: "QUESTION WAITING",
    action: "Answer",
    icon: MessageSquare,
    line: "Has a question"
  },
  failed: {
    label: "FAILED",
    action: "Open",
    icon: AlertTriangle,
    line: "Couldn't finish"
  },
  problems: {
    label: "PROBLEMS",
    action: "Open",
    icon: AlertTriangle,
    line: "Done, with problems"
  },
  finished: { label: "FINISHED", action: "Open", icon: Check, line: "Done" }
};

const URGENCY: Record<SignalFace, number> = {
  approval: 0,
  question: 1,
  failed: 2,
  problems: 3,
  finished: 4
};

const time = (iso: string | null): number => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : 0;
};

/** Most urgent kind first, then newest first. */
export const byUrgency = <T extends Signal & Pick<AttentionItem, "since">>(
  items: T[]
): T[] =>
  items
    .slice()
    .sort(
      (a, b) =>
        URGENCY[signalFace(a)] - URGENCY[signalFace(b)] ||
        time(b.since) - time(a.since)
    );

/** Tailwind classes per tone: solid (badges, chips) and soft text. */
export const TONE_CLASSES: Record<
  SignalTone,
  { solid: string; text: string; well: string; row: string }
> = {
  red: {
    solid: "bg-destructive text-white",
    text: "text-destructive",
    well: "bg-destructive/10",
    row: "bg-red-500/[0.06] hover:bg-red-500/[0.11]"
  },
  green: {
    solid: "bg-success text-white",
    text: "text-success",
    well: "bg-success-soft",
    row: "bg-emerald-500/[0.06] hover:bg-emerald-500/[0.11]"
  }
};

/**
 * Local clock time with zone ("9:21 PM EDT"), with the date in front once it
 * isn't today. Never a relative timestamp.
 */
export const signalClock = (
  iso: string | null,
  now: Date = new Date()
): string => {
  const t = time(iso);
  if (!t) return "";
  const d = new Date(t);
  const clock = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short"
  });
  if (d.toDateString() === now.toDateString()) return clock;
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
  return `${date}, ${clock}`;
};

/**
 * What needs the user: the problems when there are some, else the action.
 * One line of plain text: agents write summaries in markdown.
 */
export const signalAsk = (
  item: Pick<AttentionItem, "issues" | "summary" | "title">
): string => {
  const raw = item.issues?.trim() || item.summary?.trim() || item.title;
  return (
    markdownToPlainText(raw ?? "")
      .replace(/\s+/g, " ")
      .trim() || raw
  );
};
